import type { ItineraryItem } from '@/types/itinerary';
import type { EnRouteNote, Trip, TripCollaborator, TripPlace, TripPlaceType } from '@/types/trip';

/** A single collaborative edit — the client twin of the server's `Op`. The
 *  client stamps `clock = Date.now()` and its `actorId`, applies the op
 *  optimistically via `applyOp`, and sends it over the trip's WebSocket. */
export interface Op {
  tripId: string;
  actorId: string;
  clock: number;
  entity: 'trip' | 'place' | 'collaborator' | 'enroute';
  entityId?: string;
  type: 'set' | 'add' | 'remove';
  field?: string;
  value?: unknown;
}

/** Per-place LWW bookkeeping — the client sidecar to the server's
 *  `field_clocks` / `added_clock` / `deleted_clock`. */
export interface PlaceClocks {
  fields: Record<string, number>;
  added: number;
  deleted?: number;
}

export interface SetClocks {
  fields: Record<string, number>;
  added: number;
  deleted?: number;
}

export interface TripClocks {
  trip: Record<string, number>;
  places: Record<string, PlaceClocks>;
  collaborators: Record<string, { added: number; deleted?: number }>;
  enroute: Record<string, SetClocks>;
}

export const emptyClocks = (): TripClocks => ({
  trip: {},
  places: {},
  collaborators: {},
  enroute: {},
});

const TRIP_FIELDS = new Set([
  'name',
  'location',
  'lat',
  'lng',
  'radius',
  'startDate',
  'endDate',
  'immichAlbumId',
  'immichPhotos',
]);

const PLACE_FIELDS = new Set([
  'placeType',
  'scheduledDay',
  'scheduledDayEnd',
  'scheduledTime',
  'estimatedDurationMinutes',
  'checkInTime',
  'checkInEndTime',
  'checkOutTime',
  // Retired 2026-08-02 (see Trip.checkOutEndTime) — kept on the whitelist so
  // saves can still write null over legacy values and peers on older clients
  // don't have their ops rejected.
  'checkOutEndTime',
  'note',
  'immichPhotos',
]);

const ENROUTE_FIELDS = new Set(['text', 'direction']);

export interface ApplyResult {
  trip: Trip;
  clocks: TripClocks;
  applied: boolean;
}

const isAlive = (pc: PlaceClocks | undefined): boolean =>
  pc != null && (pc.deleted == null || pc.deleted <= pc.added);

/** Apply one op with per-field last-write-wins against the in-memory trip.
 *  Pure + immutable — returns new `{ trip, clocks }` (or the same refs +
 *  `applied: false` when the op is stale/no-op). Mirrors the server engine so
 *  optimistic local edits and incoming broadcasts converge to the same state. */
export const applyOp = (trip: Trip, clocks: TripClocks, op: Op): ApplyResult => {
  const noop: ApplyResult = { trip, clocks, applied: false };

  // ── trip scalar set ──
  if (op.entity === 'trip' && op.type === 'set' && op.field && TRIP_FIELDS.has(op.field)) {
    if (op.clock <= (clocks.trip[op.field] ?? 0)) return noop;
    return {
      trip: { ...trip, [op.field]: op.value } as Trip,
      clocks: { ...clocks, trip: { ...clocks.trip, [op.field]: op.clock } },
      applied: true,
    };
  }

  // ── place add / set / remove ──
  if (op.entity === 'place' && op.entityId) {
    const placeId = op.entityId;
    const pc = clocks.places[placeId];

    if (op.type === 'add') {
      const v = (op.value ?? {}) as Partial<TripPlace> & { item?: ItineraryItem };
      const fieldClocks: Record<string, number> = { ...(pc?.fields ?? {}) };
      const raw = v as Record<string, unknown>;
      for (const f of PLACE_FIELDS) {
        if (raw[f] !== undefined) fieldClocks[f] = op.clock;
      }
      const nextAdded = Math.max(pc?.added ?? 0, op.clock);
      const nextDeleted = pc?.deleted != null && op.clock > pc.deleted ? undefined : pc?.deleted;
      const nextPc: PlaceClocks = {
        fields: fieldClocks,
        added: nextAdded,
        ...(nextDeleted != null ? { deleted: nextDeleted } : {}),
      };

      const newPlace: TripPlace = {
        addedAt: op.clock,
        item: v.item ?? ({ id: placeId } as ItineraryItem),
        placeType: (v.placeType ?? 'activity') as TripPlaceType,
        scheduledDay: v.scheduledDay ?? null,
        scheduledDayEnd: v.scheduledDayEnd ?? null,
        scheduledTime: v.scheduledTime ?? null,
      };
      const places =
        isAlive(nextPc) && trip.places.some((p) => p.item.id === placeId)
          ? trip.places // already present + alive → idempotent
          : [...trip.places.filter((p) => p.item.id !== placeId), newPlace];
      return {
        trip: { ...trip, places },
        clocks: { ...clocks, places: { ...clocks.places, [placeId]: nextPc } },
        applied: true,
      };
    }

    if (op.type === 'set' && op.field && PLACE_FIELDS.has(op.field)) {
      const idx = trip.places.findIndex((p) => p.item.id === placeId);
      if (idx === -1) return noop;
      if (op.clock <= (pc?.fields[op.field] ?? 0)) return noop;
      const current = trip.places[idx];
      if (!current) return noop;
      const updated = { ...current, [op.field]: op.value } as TripPlace;
      return {
        trip: { ...trip, places: trip.places.map((p, i) => (i === idx ? updated : p)) },
        clocks: {
          ...clocks,
          places: {
            ...clocks.places,
            [placeId]: {
              fields: { ...(pc?.fields ?? {}), [op.field]: op.clock },
              added: pc?.added ?? 0,
              ...(pc?.deleted != null ? { deleted: pc.deleted } : {}),
            },
          },
        },
        applied: true,
      };
    }

    if (op.type === 'remove') {
      if (!pc || op.clock <= pc.added) return noop;
      if (pc.deleted != null && op.clock <= pc.deleted) return noop;
      return {
        trip: { ...trip, places: trip.places.filter((p) => p.item.id !== placeId) },
        clocks: {
          ...clocks,
          places: { ...clocks.places, [placeId]: { ...pc, deleted: op.clock } },
        },
        applied: true,
      };
    }
  }

  // ── collaborator add / remove (add/remove set, no per-field editing) ──
  if (op.entity === 'collaborator' && op.entityId) {
    const id = op.entityId;
    const cc = clocks.collaborators[id];
    const list = trip.collaborators ?? [];
    if (op.type === 'add') {
      const v = (op.value ?? {}) as TripCollaborator;
      const nextAdded = Math.max(cc?.added ?? 0, op.clock);
      const nextDeleted = cc?.deleted != null && op.clock > cc.deleted ? undefined : cc?.deleted;
      const alive = nextDeleted == null || nextDeleted <= nextAdded;
      // Collaborators are keyed by email (op.entityId is the lowercased email),
      // NOT by immichUserId — a collaborator may have no Immich account at all.
      const collaborators =
        alive && list.some((c) => c.email.toLowerCase() === id)
          ? list
          : [...list.filter((c) => c.email.toLowerCase() !== id), v];
      return {
        trip: { ...trip, collaborators },
        clocks: {
          ...clocks,
          collaborators: {
            ...clocks.collaborators,
            [id]: { added: nextAdded, ...(nextDeleted != null ? { deleted: nextDeleted } : {}) },
          },
        },
        applied: true,
      };
    }
    if (op.type === 'remove') {
      if (!cc || op.clock <= cc.added) return noop;
      if (cc.deleted != null && op.clock <= cc.deleted) return noop;
      return {
        trip: { ...trip, collaborators: list.filter((c) => c.email.toLowerCase() !== id) },
        clocks: {
          ...clocks,
          collaborators: { ...clocks.collaborators, [id]: { ...cc, deleted: op.clock } },
        },
        applied: true,
      };
    }
  }

  // ── en-route note add / set / remove ──
  if (op.entity === 'enroute' && op.entityId) {
    const id = op.entityId;
    const ec = clocks.enroute[id];
    const list = trip.enRoute ?? [];
    if (op.type === 'add') {
      const v = (op.value ?? {}) as EnRouteNote;
      const fieldClocks: Record<string, number> = { ...(ec?.fields ?? {}) };
      const raw = v as unknown as Record<string, unknown>;
      for (const f of ENROUTE_FIELDS) {
        if (raw[f] !== undefined) fieldClocks[f] = op.clock;
      }
      const nextAdded = Math.max(ec?.added ?? 0, op.clock);
      const nextDeleted = ec?.deleted != null && op.clock > ec.deleted ? undefined : ec?.deleted;
      const alive = nextDeleted == null || nextDeleted <= nextAdded;
      const note: EnRouteNote = {
        id,
        text: v.text ?? '',
        ...(v.direction !== undefined ? { direction: v.direction } : {}),
      };
      const enRoute =
        alive && list.some((n) => n.id === id) ? list : [...list.filter((n) => n.id !== id), note];
      return {
        trip: { ...trip, enRoute },
        clocks: {
          ...clocks,
          enroute: {
            ...clocks.enroute,
            [id]: {
              fields: fieldClocks,
              added: nextAdded,
              ...(nextDeleted != null ? { deleted: nextDeleted } : {}),
            },
          },
        },
        applied: true,
      };
    }
    if (op.type === 'set' && op.field && ENROUTE_FIELDS.has(op.field)) {
      const idx = list.findIndex((n) => n.id === id);
      if (idx === -1) return noop;
      if (op.clock <= (ec?.fields[op.field] ?? 0)) return noop;
      const cur = list[idx];
      if (!cur) return noop;
      const updated = { ...cur, [op.field]: op.value } as EnRouteNote;
      return {
        trip: { ...trip, enRoute: list.map((n, i) => (i === idx ? updated : n)) },
        clocks: {
          ...clocks,
          enroute: {
            ...clocks.enroute,
            [id]: {
              fields: { ...(ec?.fields ?? {}), [op.field]: op.clock },
              added: ec?.added ?? 0,
              ...(ec?.deleted != null ? { deleted: ec.deleted } : {}),
            },
          },
        },
        applied: true,
      };
    }
    if (op.type === 'remove') {
      if (!ec || op.clock <= ec.added) return noop;
      if (ec.deleted != null && op.clock <= ec.deleted) return noop;
      return {
        trip: { ...trip, enRoute: list.filter((n) => n.id !== id) },
        clocks: { ...clocks, enroute: { ...clocks.enroute, [id]: { ...ec, deleted: op.clock } } },
        applied: true,
      };
    }
  }

  return noop;
};
